import React, { useState, Suspense, useMemo, useCallback } from 'react'
import type { Components } from 'react-virtuoso'
import { Virtuoso } from 'react-virtuoso'

import { useMyReferrals } from 'src/features/business/referrals/api/referrals/use-my-referrals'
import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { SCROLL_VELOCITY_THRESHOLD } from 'src/lib/constants'
import { sendEvent } from 'src/shared/api/events/send-event'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { MapPin } from 'src/shared/components/icons/map-pin'
import { Button } from 'src/shared/components/ui/buttons/button'
import { SearchInput } from 'src/shared/components/ui/inputs/search-input'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useEventStore } from 'src/shared/store/event-store'
import { cn } from 'src/shared/utils/cn'
import { formatPrintedReferralAddress } from 'src/shared/utils/format-field-value'

export const SelectReferral = () => {
  const [search, setSearch] = useState<string | undefined>()
  const handleStartNewReferral = useSendReferralStore((state) => state.handleStartNewReferral)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value)
    }
  }

  const handleCreateNewReferral = () => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'create_new_referral',
          flowId,
        },
      })
    }

    handleStartNewReferral()
  }

  return (
    <div className="space-y-4">
      <SearchInput placeholder="Enter name, address, etc..." onChange={handleSearchChange} />
      <ReferralList search={search} />
      <div className="flex items-center justify-center space-x-6">
        <hr className="grow border-t" />
        <span className="text-sm text-primary">OR</span>
        <hr className="grow border-t" />
      </div>
      <Button onClick={handleCreateNewReferral} className="w-full">
        Create New Referral
      </Button>
    </div>
  )
}

const ReferralListContent: React.FC<{ search: string | undefined }> = ({ search }) => {
  const handleSelectReferral = useSendReferralStore((state) => state.handleSelectReferral)
  const { data: currentUser } = useMyUserData()
  const isMobile = useIsMobile()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useMyReferrals({
    query: search,
  })

  const handleSelectExistingReferral = useCallback(
    (referral: any) => {
      if (currentUser) {
        sendEvent({
          userId: currentUser.id,
          eventType: 'flow_step',
          info: {
            sessionId,
            flowType: 'send_referral',
            flowStep: 'select_existing_referral',
            flowId,
            referralId: referral.id,
          },
        })
      }

      handleSelectReferral(referral)
    },
    [currentUser, sessionId, flowId, handleSelectReferral],
  )

  const referrals = useMemo(() => {
    return data?.pages?.flatMap((page) => (Array.isArray(page) ? page : [])) ?? []
  }, [data?.pages])

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const Footer = () => {
    if (isFetchingNextPage) {
      return (
        <div className="flex h-12 w-full items-center justify-center">
          <div className="size-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )
    }
    return null
  }

  const ScrollSeekPlaceholder: Components['ScrollSeekPlaceholder'] = ({ height }) => (
    <div style={{ height }} className="flex cursor-pointer flex-col items-start border-b py-3">
      <Skeleton className="h-6 w-1/3" />
      <div className="flex items-center gap-2">
        <MapPin className="size-4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )

  if (referrals.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center">
        <p className="text-muted">It&apos;s quiet in here...</p>
      </div>
    )
  }

  return (
    <div className={cn('h-60', isMobile && 'h-[calc(100vh-380px)]')}>
      <Virtuoso
        data={referrals}
        endReached={loadMore}
        overscan={200}
        itemContent={(index, referral) => (
          <div
            key={referral.id}
            className="flex cursor-pointer flex-col items-start border-b py-3"
            onClick={() => handleSelectExistingReferral(referral)}
          >
            <p className="text-xl font-semibold">
              {referral.clientFirstName} {referral.clientLastName}
            </p>
            <p className="flex items-center gap-2 text-sm text-muted">
              <MapPin className="size-4" />
              {formatPrintedReferralAddress(referral)}
            </p>
          </div>
        )}
        components={{
          Footer,
          ScrollSeekPlaceholder,
        }}
        scrollSeekConfiguration={{
          enter: (velocity) => Math.abs(velocity) > SCROLL_VELOCITY_THRESHOLD,
          exit: (velocity) => Math.abs(velocity) < 10,
        }}
        style={{ height: '100%', scrollbarWidth: 'none' }}
      />
    </div>
  )
}

const ReferralList: React.FC<{ search: string | undefined }> = ({ search }) => {
  return (
    <ErrorBoundaryWrapper title="Sorry, there was an error loading your referrals" description="Please try again later">
      <Suspense fallback={<SkeletonList />}>
        <ReferralListContent search={search} />
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}

const SkeletonList = () => {
  return (
    <>
      {Array(5)
        .fill(null)
        .map((_, index) => (
          <div key={index} className="flex flex-col items-start gap-3 border-b py-3 opacity-60">
            <Skeleton className="h-6 w-1/3" />
            <div className="flex items-center gap-2">
              <MapPin className="size-4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        ))}
    </>
  )
}
